import React, { useEffect, useRef, useState } from "react";
import { FieldInput, SearchableSelect, DetailRow } from "../../_components/Purchasing/PurchaseShared";
import { FiHash } from "react-icons/fi";
import { getPurchaseOrders, getPurchaseOrderById } from "../../_services/purchase.service";
import { getSuppliersByName } from "../../_services/contact.service";

// Shared by the Direct and Indirect Purchase Invoice pages, which differ only
// in labels and in whether the PO Reference is required.

/* Supplier data fetchers */
export async function fetchContactPersons(supplierName) {
    if (!supplierName) return [];
    const data = await getSuppliersByName(supplierName);
    return data.filter((r) => { const s = (r.status || r.Status || "").toLowerCase(); return r.name === supplierName && !!r.attention && s === "active"; }).map((r) => r.attention ?? "").filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
}
export async function fetchSupplierDetails(supplierName, contactPerson = "") {
    if (!supplierName) return {};
    const data = await getSuppliersByName(supplierName);
    const rows = data.filter((r) => r.name === supplierName);
    const first = rows[0];
    if (!first) return {};
    const base = { supplierAddress: first.billingAddress || first.billing_address || first.address || "", supplierShippingAddress: first.shippingAddress || first.shipping_address || "" };
    if (!contactPerson) return base;
    const match = rows.find((r) => (r.attention || "") === contactPerson) || first;
    return { ...base, paymentTerms: match.creditTerms || match.credit_terms || "", phoneNumber: match.phoneNumber || match.phone_number || "" };
}

const sameText = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

// Only Approved POs can be linked (the server checks this too). Asked for
// directly, instead of loading every PO and filtering here.
const APPROVED_PO_PAGE_SIZE = 5000;

// Header fields copied from the chosen PO when it has them.
const PO_HEADER_FIELDS = ["contactPerson", "phoneNumber", "paymentTerms", "supplierAddress"];

/**
 * Puts the PO's items on the form together with the currency their prices
 * are in (and the PO's rate), so a USD PO's items aren't saved as MYR. The
 * currency goes first: changing it converts the prices of the items being
 * replaced, and then the PO's items (already in that currency) go in.
 */
function applyPoItems(onChange, { items, currency, exchangeRate }) {
    if (currency) {
        onChange({ target: { name: "currency", value: currency } });
        onChange({ target: { name: "exchangeRate", value: exchangeRate ?? null } });
    }
    onChange({ target: { name: "items", value: items } });
}

/**
 * PO Reference No. + supplier reference fields for a Purchase Invoice page.
 * Returns { extraFields }: pass it to PurchaseDocDetails.
 *  - The dropdown lists the supplier's Approved POs, plus the PO already
 *    saved on the invoice (which may no longer be in that list).
 *  - Picking a PO fills the contact person, phone, payment terms, address and
 *    line items (with their currency) from it, replacing any items entered.
 */
export function usePurchaseInvoiceReferenceFields({ piType }) {
    const isDirect = piType === "Direct";
    const [approvedPos, setApprovedPos] = useState([]);
    const alive = useRef(true);

    useEffect(() => {
        alive.current = true;
        getPurchaseOrders({ status: "Approved", pageSize: APPROVED_PO_PAGE_SIZE })
            .then((res) => { if (alive.current) setApprovedPos(res?.data || []); })
            .catch(() => { });
        return () => { alive.current = false; };
    }, []);

    const handlePoChange = async (e, onChange) => {
        const poId = e.target.value;
        onChange({ target: { name: "poRefNo", value: poId } });
        if (!poId) return;

        let po = approvedPos.find((p) => sameText(p.id, poId));
        try {
            po = (await getPurchaseOrderById(poId)) || po; // full PO, with its items
        } catch { /* keep the list copy */ }
        if (!po || !alive.current) return;

        PO_HEADER_FIELDS.forEach((name) => {
            if (po[name]) onChange({ target: { name, value: po[name] } });
        });
        if (!po.items?.length) return;
        applyPoItems(onChange, { items: po.items, currency: po.currency || null, exchangeRate: po.exchangeRate ?? null });
    };

    const extraFields = {
        left: ({ draft, onChange, errors, edit }) => {
            if (edit) {
                const supplierPos = approvedPos
                    .filter((po) => sameText(po.supplier, draft.supplier))
                    .map((po) => po.id)
                    .filter(Boolean);
                // Editing an invoice whose PO is no longer listed (e.g. not
                // Approved any more) still shows that PO.
                const options = draft.poRefNo && !supplierPos.includes(draft.poRefNo)
                    ? [draft.poRefNo, ...supplierPos]
                    : supplierPos;
                return (
                    <>
                        <SearchableSelect
                            label="PO Reference No."
                            name="poRefNo"
                            value={draft.poRefNo || ""}
                            options={options}
                            onChange={(e) => handlePoChange(e, onChange)}
                            placeholder={isDirect ? "Select PO reference..." : "Select PO reference... (Optional)"}
                            // Unlocked once the supplier and contact person are picked, as in Sales.
                            disabled={!draft.supplier || !draft.contactPerson}
                            required={isDirect}
                            error={errors?.poRefNo}
                        />
                        <FieldInput
                            label={isDirect ? "Inv Reference No" : "Reference No."}
                            name="refNo"
                            value={draft.refNo || ""}
                            onChange={onChange}
                            required
                            error={errors?.refNo}
                            placeholder={isDirect ? "Enter invoice reference no..." : "Enter reference number..."}
                        />
                    </>
                );
            }
            return (
                <>
                    {draft.poRefNo && (
                        <DetailRow icon={FiHash} label="PO Reference No.">{draft.poRefNo}</DetailRow>
                    )}
                    {draft.refNo && (
                        <DetailRow icon={FiHash} label={isDirect ? "Inv Reference No" : "Reference No."}>{draft.refNo}</DetailRow>
                    )}
                </>
            );
        },
    };

    return { extraFields };
}
